import React from 'react';
import PropTypes from 'prop-types';
// utils
import { hardReset, isStaleBuildError } from '../utils/hardReset';

// ----------------------------------------------------------------------

// Per-route safety net, sitting above each lazily loaded page in
// routes/index.js.
//
// AppErrorBoundary catches everything, but it is the outermost boundary, so a
// single page failing takes the navbar, the sidebar and the router down with
// it and the user has nowhere to go but a full-screen error. The common case
// does not deserve that: a lazy import rejects because the deploy replaced the
// chunk, which is about this page and nothing else.
//
// Same rules as AppErrorBoundary - plain DOM, inline styles, no MUI, no theme,
// no context. This renders while the tree around it is broken.

const wrap = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 320,
  padding: 24,
  boxSizing: 'border-box',
  fontFamily: '"Public Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
};

const button = {
  appearance: 'none',
  border: 'none',
  borderRadius: 8,
  padding: '10px 18px',
  fontSize: 14,
  fontWeight: 600,
  fontFamily: 'inherit',
  cursor: 'pointer',
  background: '#8B2FD0',
  color: '#FFFFFF',
};

class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    // eslint-disable-next-line no-console
    console.error('[DeskOS] route error\n', error?.message, '\n', errorInfo?.componentStack, '\n', error);

    if (isStaleBuildError(error)) {
      // The page this route needs is from a build the server no longer has.
      // Purge and come back on the current one. hardReset() will refuse if it
      // already tried recently, and then the panel below stays put.
      hardReset({ to: window.location.pathname + window.location.search });
    }
  }

  render() {
    const { error } = this.state;
    const { children } = this.props;

    if (!error) return children;

    const stale = isStaleBuildError(error);

    return (
      <div style={wrap}>
        <div style={{ maxWidth: 420, textAlign: 'center' }}>
          <h2 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700, color: '#212B36' }}>
            {stale ? 'This page has been updated' : 'This page could not be opened'}
          </h2>
          <p style={{ margin: '0 0 20px', fontSize: 14, lineHeight: 1.6, color: '#637381' }}>
            {stale
              ? 'A new version of DeskOS was released while you had this open. Reload to pick it up.'
              : 'Something went wrong while drawing this screen. The rest of the app is still working.'}
          </p>
          <button
            type="button"
            style={button}
            onClick={() => hardReset({ to: window.location.pathname + window.location.search, force: true })}
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}

RouteErrorBoundary.propTypes = {
  children: PropTypes.node,
};

export default RouteErrorBoundary;
